/**
 * TalentEngine roles as returned by the backend (`GET /api/users/tenant-info`).
 * The backend resolves them from Intelliconvo; the frontend only reads them.
 * Import these instead of writing role strings in components.
 */
export const ROLES = Object.freeze({
  ADMIN: "admin",
  MANAGER: "manager",
  RECRUITER: "recruiter",
  SUPPLIER: "supplier",
});

// Error code the backend sends with 403 when a user has no TalentEngine role.
export const NO_ACCESS_CODE = "TE_NO_ACCESS";

/** Normalise a stored role string to one of ROLES, or null. */
export const normalizeRole = (role) => {
  const value = String(role ?? "").trim().toLowerCase();
  return Object.values(ROLES).includes(value) ? value : null;
};

// Our own people (as opposed to external suppliers).
export const STAFF_ROLES = Object.freeze([ROLES.ADMIN, ROLES.MANAGER, ROLES.RECRUITER]);

/** True when the role is one of `roles`. Accepts stored values like "Admin". */
export const hasRole = (role, roles) => {
  const normalized = normalizeRole(role);
  return Boolean(normalized) && roles.includes(normalized);
};

export const isStaffRole = (role) => hasRole(role, STAFF_ROLES);

// Roles allowed to delete candidates (the backend enforces its own check).
export const CANDIDATE_DELETE_ROLES = Object.freeze([ROLES.ADMIN, ROLES.MANAGER, ROLES.RECRUITER]);

// Display names for each role (e.g. in the navbar).
export const ROLE_LABELS = Object.freeze({
  [ROLES.ADMIN]: "Admin",
  [ROLES.MANAGER]: "Manager",
  [ROLES.RECRUITER]: "Recruiter",
  [ROLES.SUPPLIER]: "Supplier",
});

/** Display name for a role, or "" for an unknown role. */
export const getRoleLabel = (role) => ROLE_LABELS[normalizeRole(role)] || "";

// Requisitions: recruiters raise them, managers approve them in Approvals (the
// backend enforces its own check). Managers may also edit/delete any
// requisition; others only their own while it is still pending.
export const REQUISITION_MANAGE_ALL_ROLES = Object.freeze([ROLES.MANAGER]);
